import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
  <div className="min-h-screen bg-background">
    <Header />
    <main className="container mx-auto px-4 py-24 max-w-3xl">
      <Button variant="ghost" size="sm" className="mb-6 -ml-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </Button>
      <h1 className="text-3xl font-display font-bold text-foreground mb-6">Cookie Policy</h1>
      <div className="prose text-muted-foreground space-y-4 text-sm">
        <p>Last updated: October 2026</p>

        <p>
          This Cookie Policy explains what cookies are, how Jynio ("we", "us", or "our") uses them on
          jynio.co (the "Service"), and your choices regarding cookies. This policy should be read alongside
          our <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">1. What Are Cookies</h2>
        <p>
          Cookies are small text files that are stored on your device (computer, tablet, or mobile) when
          you visit a website. They are widely used to make websites work efficiently and to provide
          information to site owners. We also use localStorage and sessionStorage, similar browser storage
          mechanisms, to remember your preferences and support platform functionality.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">2. How We Use Cookies</h2>
        <p>
          We use cookies to keep you signed in, remember your preferences, and (with your consent)
          understand how you use our platform so we can improve it. Google Analytics is only loaded
          after you have given explicit consent: before that, nothing is sent to it.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">3. Types of Cookies We Use</h2>

        <h3 className="text-base font-semibold text-foreground mt-4">Essential Cookies & Storage</h3>
        <p>
          These cookies and browser storage items are strictly necessary for the Service to function.
          They cannot be disabled.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-border">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 border-b border-border">Name</th>
                <th className="text-left p-2 border-b border-border">Type</th>
                <th className="text-left p-2 border-b border-border">Purpose</th>
                <th className="text-left p-2 border-b border-border">Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border-b border-border"><code>session_id</code></td>
                <td className="p-2 border-b border-border">Cookie (signed)</td>
                <td className="p-2 border-b border-border">Keeps you signed in to your account</td>
                <td className="p-2 border-b border-border">Permanent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>_server_session</code></td>
                <td className="p-2 border-b border-border">Cookie</td>
                <td className="p-2 border-b border-border">Holds the security token that protects our forms and the state of a sign-in with Google</td>
                <td className="p-2 border-b border-border">Browser session</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>cookie-consent</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Stores your cookie consent preferences</td>
                <td className="p-2 border-b border-border">Persistent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.locale</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers the language you chose for the site</td>
                <td className="p-2 border-b border-border">Persistent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.classroom.devices</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers which camera and microphone you picked for lessons</td>
                <td className="p-2 border-b border-border">Persistent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio_referral_code</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Keeps the referral code of the invitation link you followed until you create your account</td>
                <td className="p-2 border-b border-border">Until sign-up</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio:push-banner-dismissed-at</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers when you closed the notifications banner, so it does not come back for 7 days</td>
                <td className="p-2 border-b border-border">Persistent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.flashcards.reminderOffer.denied</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers that your browser refused notifications, so we stop offering the daily flashcard reminder</td>
                <td className="p-2 border-b border-border">Persistent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.flashcards.runningImport</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers a flashcard import that is still running, so its progress survives leaving the page</td>
                <td className="p-2 border-b border-border">Until the import ends</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.flashcards.lessonBand.dismissed:&lt;lesson&gt;</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers that you closed the flashcards banner of a given lesson</td>
                <td className="p-2 border-b border-border">Persistent</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.tour.resumeStep</code></td>
                <td className="p-2 border-b border-border">localStorage</td>
                <td className="p-2 border-b border-border">Remembers where you stopped in the guided tour, so you can pick it up again</td>
                <td className="p-2 border-b border-border">Until the tour ends</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.tour.dismissedThisSession</code></td>
                <td className="p-2 border-b border-border">sessionStorage</td>
                <td className="p-2 border-b border-border">Remembers that you chose "not now" for the guided tour in this tab</td>
                <td className="p-2 border-b border-border">Tab session</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>jynio.chunkReloadAt</code></td>
                <td className="p-2 border-b border-border">sessionStorage</td>
                <td className="p-2 border-b border-border">Stops the page from reloading itself in a loop after an update of the site</td>
                <td className="p-2 border-b border-border">Tab session</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-base font-semibold text-foreground mt-4">Analytics Cookies (require consent)</h3>
        <p>
          These cookies help us understand how visitors interact with the Service by collecting
          anonymised data. They are set by Google Analytics 4 (measurement ID: G-6TLZBSE756) and are
          only activated after you consent through our cookie banner.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-border">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 border-b border-border">Name</th>
                <th className="text-left p-2 border-b border-border">Type</th>
                <th className="text-left p-2 border-b border-border">Purpose</th>
                <th className="text-left p-2 border-b border-border">Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border-b border-border"><code>_ga</code></td>
                <td className="p-2 border-b border-border">Cookie</td>
                <td className="p-2 border-b border-border">Distinguishes unique users</td>
                <td className="p-2 border-b border-border">2 years</td>
              </tr>
              <tr>
                <td className="p-2 border-b border-border"><code>_ga_6TLZBSE756</code></td>
                <td className="p-2 border-b border-border">Cookie</td>
                <td className="p-2 border-b border-border">Maintains session state</td>
                <td className="p-2 border-b border-border">2 years</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          We also use <strong>Sentry</strong> for error monitoring (it records technical details of
          errors so we can fix them). In your browser it is treated as an analytics service and only
          loads after you consent to analytics cookies. Separately, when something fails on our servers,
          the server sends a report to Sentry whatever your cookie choice: that is not a cookie, and it
          carries your account ID when you are signed in, never your email address. See our{" "}
          <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
        <p>
          A tutor's introduction video may be a <strong>YouTube</strong> or <strong>Vimeo</strong> link.
          Those players load on the tutor's profile page and may set their own cookies, which we do not
          control. See their privacy policies.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">Marketing Cookies (require consent)</h3>
        <p>
          We use <strong>OneSignal</strong> to deliver web push notifications (e.g. lesson reminders
          and messages) if you opt in. The OneSignal SDK and its storage only load after you consent
          to marketing cookies; until then it is not loaded at all.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">4. Managing Your Cookie Preferences</h2>
        <p>
          You can manage your cookie preferences in the following ways:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Cookie banner:</strong> when you first visit the Service, a banner lets you accept all cookies, accept only essential cookies, or customise your preferences.</li>
          <li><strong>Update your preferences:</strong> you can change your cookie choices at any time using the link below.</li>
          <li><strong>Browser settings:</strong> most browsers allow you to block or delete cookies through their settings. Note that blocking essential cookies may impair the functionality of the Service.</li>
          <li><strong>Google Analytics opt-out:</strong> you can install the <a href="https://tools.google.com/dlpage/gaoptout" className="underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.</li>
        </ul>
        <p className="mt-2">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-cookie-prefs"))}
            className="underline hover:text-foreground transition-colors"
          >
            Manage cookie settings
          </button>
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">5. Updates to This Policy</h2>
        <p>
          We may update this Cookie Policy from time to time. Changes will be posted on this page with a
          revised "Last updated" date.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">6. Contact</h2>
        <p>
          For questions about this Cookie Policy, contact us at{" "}
          <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>.
        </p>
      </div>
    </main>
    <Footer />
  </div>
  );
};

export default CookiePolicy;
